import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { Customer, CustomerService } from 'src/app/shared/services/customer.service';
import { StockService } from 'src/app/stock.service';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { StockItemDTO, StockItemDTOForCustomer } from 'src/app/domain/stock';
import { saveAs } from 'file-saver';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerWithStockItems } from 'src/app/domain/customer-with-stock';

@Component({
  selector: 'app-customer-orders',
  templateUrl: './customer-orders.component.html',
  styleUrls: ['./customer-orders.component.scss']
})
export class CustomerOrdersComponent implements OnInit {
  @ViewChild('codt') table: Table;

  customerOptions: CustomerWithStockItems[] = [];
  selectedCustomer: number | null = null;
  visibleCustomerOptions: any[] = [];
  totalRecords: number = 0;
  stockItems: StockItemDTOForCustomer[] = [];
  editDialogVisible: boolean = false;
  selectedStockItem: StockItemDTOForCustomer;
  itemsDialog: boolean = false;
  today: Date = new Date();

  constructor(
    private messageService: MessageService,
    private customerService: CustomerService,
    private stockService: StockService,
    private router: Router  ,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef

  ) { }

  async ngOnInit() {
    this.stockService.getCustomersWithStockItems().subscribe(
      response => {
        this.customerOptions = response;
        this.totalRecords = this.customerOptions.length;
        this.loadCustomersLazy({ first: 0, rows: 38 });
        
        // Check for route parameters after loading customers
        let hasRouteParam = false;
        this.route.queryParamMap.subscribe(params => {
          const customerId = params.get('customerId');
          if (customerId) {
            hasRouteParam = true;
            this.selectedCustomer = parseInt(customerId, 10);
            this.onCustomerChange();
          }
        });
        
        const navigation = this.router.getCurrentNavigation();
        if (navigation?.extras?.state) {
          const customerId = navigation.extras.state['customerId'];
          if (customerId) {
            hasRouteParam = true;
            this.selectedCustomer = typeof customerId === 'string' ? parseInt(customerId, 10) : customerId;
            this.onCustomerChange();
          }
        }
        
        // If no route parameter, default to "All Customers" (id: 0)
        if (!hasRouteParam) {
          this.selectedCustomer = 0;
          this.onCustomerChange();
        }
      },
      error => {
        console.error('Error fetching customers with stock items:', error);
      }
    );
  }
  loadCustomersLazy(event: any) {
  const start = event.first;
  const end = start + event.rows;

  let customers = this.customerOptions.slice(start, end);

  // Only prepend "All Customers" on the first page
  if (start === 0) {
    customers = [{ 
      id: 0, 
      customerName: 'All Customers', 
      customerNumber: '', 
      contactPersonName: '', 
      mobileNumber: '', 
      email: '', 
      customerGroupName: null 
    }, ...customers];
  }

  this.visibleCustomerOptions = customers;
}

onCustomerChange() {
  if (this.selectedCustomer && this.selectedCustomer !== 0) {
    this.stockService.getStockItemsByCustomerId(this.selectedCustomer).subscribe(
      (data: StockItemDTOForCustomer[]) => {
      this.stockItems = data.map(item => {
        const expiryDate = item.itemExpiryDate ? new Date(item.itemExpiryDate) : null;

        return {
          ...item,
          itemPurchaseDate: item.itemPurchaseDate ? new Date(item.itemPurchaseDate) : null,
          itemExpiryDate: expiryDate,
          expiredAfter: expiryDate ? this.calculateDaysDifference(expiryDate) : 0,
          status: expiryDate && this.calculateDaysDifference(expiryDate) < 1 ? 'Expired' : 'Active',
          sbmStock: !item.customerStock
        };
      });
      }
    );
  } else {
    this.stockService.getAllCustomerStockItems().subscribe(
      (data: StockItemDTOForCustomer[]) => {
       this.stockItems = data.map(item => {
        const expiryDate = item.itemExpiryDate ? new Date(item.itemExpiryDate) : null;

        return {
          ...item,
          itemPurchaseDate: item.itemPurchaseDate ? new Date(item.itemPurchaseDate) : null,
          itemExpiryDate: expiryDate,
          expiredAfter: expiryDate ? this.calculateDaysDifference(expiryDate) : 0,
          status: expiryDate && this.calculateDaysDifference(expiryDate) < 1 ? 'Expired' : 'Active',
          sbmStock: !item.customerStock

        };
      });

      }
    );
  }
}



  editStockItem(stockItem: StockItemDTOForCustomer) {
    this.selectedStockItem = { ...stockItem };
    this.editDialogVisible = true;
  }

  closeEditDialog() {
    this.editDialogVisible = false;
  }

  saveStockItem() {
    this.stockService.updateStockItemsForCustomer([this.selectedStockItem]).subscribe(
      response => {
        const index = this.stockItems.findIndex(item => item.id === this.selectedStockItem.id);
        if (index !== -1) {
          this.stockItems[index] = { ...this.selectedStockItem };
          this.stockItems = [...this.stockItems];
          this.cdr.detectChanges(); // Manually trigger change detection
        }
        this.messageService.add({ 
          severity: 'info', 
          summary: 'Order Details Saved', 
          detail: 'Order detail updated successfully!' 
        });
        this.closeEditDialog();
      },
      error => {
        console.error('Error updating order item:', error);
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Update Failed', 
          detail: 'Failed to update order detail.' 
        });
      }
    );
  }
  exportExcel() {
    const dataToExport = this.table.filteredValue || this.stockItems;
    const worksheet = XLSX.utils.json_to_sheet(dataToExport.map(item => ({
      'Customer Name': item.customerName,
      'Product Category': item.productCategory,
      'Product Type': item.productType,
      'Item Name': item.productItem,
      'Serial Number': item.serialNumber,
      'Coverage Start Date': item.itemPurchaseDate,
      'Coverage End Date': item.itemExpiryDate,
      'Expired After': item.expiredAfter,
      'Absolute Item': item.absoluteItem ? 'Yes' : 'No',
      'Order Number': item.orderNumber,
      'Cost Price': item.costPrice,
      'Selling Price': item.sellingPrice,
    })));

    const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, "customer_orders");
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
  }
  exportPdf() {
    let dataToExport = this.table.filteredValue?.length ? this.table.filteredValue : this.stockItems;
  
    //  expirdAfter is calculated for all items
    dataToExport = dataToExport.map(item => ({
      ...item,
      expiredAfter: item.expiredAfter ?? this.calculateDaysDifference(new Date(item.itemExpiryDate))
    }));
  
    const doc = new jsPDF('landscape', 'pt', 'a4');
  
    const columns = [
      { header: 'Customer Name', dataKey: 'customerName' },
      { header: 'Product Category', dataKey: 'productCategory' },
      { header: 'Product Type', dataKey: 'productType' },
      { header: 'Item Name', dataKey: 'productItem' },
      { header: 'Serial Number', dataKey: 'serialNumber' },
      { header: 'Coverage Start Date', dataKey: 'itemPurchaseDate' },
      { header: 'Coverage End Date', dataKey: 'itemExpiryDate' },
      { header: 'Expired After', dataKey: 'expiredAfter' },
      { header: 'Absolute Item', dataKey: 'absoluteItem' },
      { header: 'Order Number', dataKey: 'orderNumber' },
      { header: 'Cost Price', dataKey: 'costPrice' },
      { header: 'Selling Price', dataKey: 'sellingPrice' },
    ];
  
    const rows = dataToExport.map(item => ({
      customerName: item.customerName ?? 'No Customer',
      serialNumber: item.serialNumber ?? 'No Serial',
      itemPurchaseDate: item.itemPurchaseDate ? new Date(item.itemPurchaseDate).toLocaleDateString() : 'No Date',
      itemExpiryDate: item.itemExpiryDate ? new Date(item.itemExpiryDate).toLocaleDateString() : 'No Date',
      expiredAfter: item.expiredAfter ?? 'N/A',
      absoluteItem: item.absoluteItem ? 'Yes' : 'No',
      orderNumber: item.orderNumber ?? 'No Orders',
      costPrice: item.costPrice,
      sellingPrice: item.sellingPrice,
      productCategory: item.productCategory ?? 'No Category',
      productType: item.productType ?? 'No Type',
      productItem: item.productItem ?? 'No Item'
    }));
  
    autoTable(doc, {
      columns: columns,
      body: rows,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 10,
        cellPadding: 2,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240]
      },
      margin: { top: 20 }
    });
  
    doc.save('Customer_Orders.pdf');
  }
  
  editStockItemDialog() {
    this.itemsDialog = true;
  }

  closeDialog() {
    this.itemsDialog = false;
  }

  saveStockItems() {
    this.stockService.updateStockItemsForCustomer(this.stockItems).subscribe(
      (updatedItems) => {
        this.stockItems = updatedItems;
        this.closeDialog();
      },
      (error) => {
        console.error('Failed to update stock items:', error);
      }
    );
  }

  navigateToOrderItemsDetailsPage(stockItem: StockItemDTO) {
    // console.log('Navigating with stock item data:', stockItem);

    this.router.navigate(['/orders-items'], {
      state: { data: stockItem}
    });

  }
  calculateDaysDifference(endDate: Date): number {
    const currentDate = new Date();
    const timeDiff = endDate.getTime() - currentDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff;
  }

  get selectedCustomerName(): string {
    debugger
  if (!this.selectedCustomer || this.selectedCustomer === 0) return 'All Customers';
  const customer = this.customerOptions.find(c => c.id === this.selectedCustomer);
  return customer?.customerName || 'All Customers';
}

}
